import * as HLF from "html-living-framework";
import * as Constants from "../Data/Constants";
import * as DollData from "../Data/DollData";
import { Paintable } from "../GameObjects/Paintable";
import { PaintableLayer } from "../GameObjects/PaintableLayer";

export class PaintableDoll extends Paintable {
    private _topOutline: PIXI.Sprite;
    private _botOutline: PIXI.Sprite;
    private _botInside!: PIXI.Sprite;
    private _topPieces: PaintableLayer[];
    private _botPieces: PaintableLayer[];
    private _topContainer: PIXI.Container;
    private _botContainer: PIXI.Container;

    constructor(pIndex: number, pCreateInput: boolean = true, pGalleryItem = false) {
        super(pIndex);
        this._topPieces = new Array<PaintableLayer>();
        this._botPieces = new Array<PaintableLayer>();
        this._topContainer = new PIXI.Container();
        this._botContainer = new PIXI.Container();

        const sheet: string = Constants.SPRITESHEET_DOLL_PREFIX + HLF.NumberUtils.addZeroPadding(pIndex, 2);
        const prefix: string = "doll" + pIndex + "-";
        const pieceCount: number = DollData.DATA[pIndex].layerCount;
        let layer: PaintableLayer;

        for (let i = 0; i < pieceCount; ++i) {
            const index: number = i;
            layer = new PaintableLayer(
                prefix + "bot-" + HLF.NumberUtils.addZeroPadding(i.toString(), 2),
                sheet,
                () => { this.onLayerPainted(index); },
                pCreateInput ? DollData.DATA[pIndex].map : undefined,
                pCreateInput ? i : undefined,
            );
            layer.scale.set(2);
            this._botPieces.push(layer);
            this._layers.push(this._botPieces[i]);
            this._botContainer.addChild(this._botPieces[i]);
        }

        this._botInside = HLF.PixiUtils.makeSprite(prefix + "bot-inside", sheet);
        this.addChild(this._botInside);

        this.addChild(this._botContainer);
        this._botOutline = HLF.PixiUtils.makeSprite(prefix + "bot-outline", sheet);
        this._botContainer.addChild(this._botOutline);

        for (let i = 0; i < pieceCount; ++i) {
            const index: number = i;
            layer = new PaintableLayer(
                prefix + "top-" + HLF.NumberUtils.addZeroPadding(i.toString(), 2),
                sheet,
                () => { this.onLayerPainted(this._botPieces.length + index); },
            );
            layer.scale.set(2);
            this._topPieces.push(layer);
            this._layers.push(this._topPieces[i]);
            this._topContainer.addChild(this._topPieces[i]);
        }
        this.addChild(this._topContainer);
        this._topOutline = HLF.PixiUtils.makeSprite(prefix + "top-outline", sheet);
        this._topContainer.addChild(this._topOutline);

        for (let i = 0; i < pieceCount; ++i) {
            this._botPieces[i].partner = this._topPieces[i];
            this._topPieces[i].partner = this._botPieces[i];
        }
    }

    public get topContainer(): PIXI.Container {
        return this._topContainer;
    }

    public get botContainer(): PIXI.Container {
        return this._botContainer;
    }

    public get botInside(): PIXI.Container {
        return this._botInside;
    }

    // component getter/setters are convenience functions used by GalleryDoll animation
    public get componentScaleX(): number {
        return this._topContainer.scale.x;
    }

    public set componentScaleX(pValue: number) {
        this._topContainer.scale.x = pValue;
        this._botContainer.scale.x = pValue;
        this._botInside.scale.x = pValue;
    }
    public get componentScaleY(): number {
        return this._topContainer.scale.x;
    }

    public set componentScaleY(pValue: number) {
        this._topContainer.scale.y = pValue;
        this._botContainer.scale.y = pValue;
        this._botInside.scale.y = pValue;
    }

    public get componentPosition(): PIXI.Point {
        return this._topContainer.position;
    }

    public set componentPosition(pValue: PIXI.Point) {
        this._topContainer.position.copy(pValue);
        this._botContainer.position.copy(pValue);
        this._botInside.position.copy(pValue);
    }

    public get componentPositionX(): number {
        return this._topContainer.position.x;
    }

    public set componentPositionX(pValue: number) {
        this._topContainer.position.x = pValue;
        this._botContainer.position.x = pValue;
        this._botInside.position.x = pValue;
    }

    public get componentPositionY(): number {
        return this._topContainer.position.y;
    }

    public set componentPositionY(pValue: number) {
        this._topContainer.position.y = pValue;
        this._botContainer.position.y = pValue;
        this._botInside.position.y = pValue;
    }

    public onLayerPainted(pIndex: number): void {
        // Find the index of the bottom piece, even if top was painted
        const layerCount: number = DollData.DATA[this._index].layerCount;
        const isTop: boolean = pIndex >= layerCount;
        const commonIndex: number = isTop ? pIndex - layerCount : pIndex;

        // Add one entry to the sequence for both top+bottom
        const existing: number = this._sequence.indexOf(commonIndex);
        if (existing !== -1) {
            this._sequence.splice(existing, 1);
        }
        if (this._layers[pIndex].paintedColour !== 0xFFFFFF) {
            this._sequence.push(commonIndex);
        }
    }

    public clear(): void {
        for (let i = 0; i < this._botPieces.length; ++i) {
            this._botPieces[i].paint(0xFFFFFF, 0.5, 0.5, false, true, false, true);
        }
    }

    public swapToLowRes(pSizeIndex: number): void {
        const prefix: string = "doll" + this._index + "-";
        const suffix: string = "-small" + pSizeIndex;
        const sheet: string = Constants.SPRITESHEET_DOLL_PREFIX + "0" + this._index;

        this._topOutline.texture = PIXI.loader.resources[sheet].textures![prefix + "top" + suffix];
        this._topOutline.scale.set(1 / this.componentScaleX);
        this._botOutline.texture = PIXI.loader.resources[sheet].textures![prefix + "bot" + suffix];
        this._botOutline.scale.copy(this._topOutline.scale);
        this._botInside.visible = false;

        if (this._index === 1) {
            for (let i = 0; i < this._layers.length; ++i) {
                this._layers[i].scale.set(2 * 0.975);
            }
        }
    }
}
